import { farmavel } from '../../js/repo/farmavel'

/**
 * Farmavel SDK (Laravel Version)
 *
 * Este SDK interact?a con el nuevo backend Farmavel desarrollado en Laravel,
 * utilizando autenticaci?n Sanctum y rutas RESTful est?ndar.
 */

/**
 * Realiza el inicio de sesi?n del administrador.
 * En Laravel Sanctum, esto suele devolver un token de acceso.
 *
 * @param {string} username - Nombre de usuario del administrador.
 * @param {string} password - Contrase?a.
 * @returns {Promise<Object>} Datos del usuario y token.
 */
export const adminLogin = async (username, password) => {
    const response = await farmavel.post('v1/admin/login', {
        username,
        password,
    })

    // Si el backend devuelve un token, lo guardamos para el interceptor de axios
    if (response.token) {
        localStorage.setItem('farmavel_token', response.token)
    }

    return response
}

/**
 * Obtiene los datos de configuraci?n del panel de administraci?n.
 */
export const getConfigData = async () => {
    return farmavel.get('v1/admin/config')
}

/**
 * Cierra la sesi?n del administrador.
 */
export const logout = async () => {
    try {
        await farmavel.post('logout')
    } finally {
        localStorage.removeItem('farmavel_token')
    }
}

/**
 * Obtiene la lista de farmacias para administrar.
 *
 * @param {Object} params - Par?metros de filtrado y paginaci?n.
 * @returns {Promise<Object>} Lista de farmacias.
 */
export const getPharms = async (params = {}) => {
    return farmavel.get('v1/admin/pharms', params)
}

/**
 * Obtiene la informaci?n detallada de una farmacia.
 *
 * @param {number|string} id - ID de la farmacia.
 * @returns {Promise<Object>} Datos de la farmacia.
 */
export const getPharmData = async (id) => {
    return farmavel.get(`v1/admin/pharms/${id}`)
}

/**
 * Crea una nueva farmacia.
 *
 * @param {Object} data - Datos de la farmacia.
 * @returns {Promise<Object>} Farmacia creada.
 */
export const createPharm = async (data) => {
    return farmavel.post('v1/admin/pharms', data)
}

/**
 * Actualiza la informaci?n de una farmacia.
 *
 * @param {number|string} id - ID de la farmacia.
 * @param {Object} data - Datos a actualizar.
 * @returns {Promise<Object>} Resultado de la actualizaci?n.
 */
export const updatePharm = async (id, data) => {
    return farmavel.put(`v1/admin/pharms/${id}`, data)
}

/**
 * Elimina (baja logica) una farmacia. Requiere superadmin (user_role === 1).
 *
 * @param {number|string} id - ID de la farmacia.
 * @returns {Promise<Object>} Resultado de la operacion.
 */
export const deletePharm = async (id) => {
    return farmavel.delete(`v1/admin/pharms/${id}`)
}

/**
 * Obtiene la lista de medicamentos.
 *
 * @param {Object} params - Par?metros de filtrado y ordenaci?n.
 * @returns {Promise<Object>} Lista de medicamentos.
 */
export const getDrugs = async (params = {}) => {
    return farmavel.get('v1/drugs', params)
}

/**
 * Obtiene la informaci?n detallada de un medicamento.
 *
 * @param {number|string} id - ID del medicamento.
 * @returns {Promise<Object>} Datos del medicamento.
 */
export const getDrugData = async (id) => {
    return farmavel.get(`v1/drugs/${id}`)
}

/**
 * Actualiza la informaci?n de un medicamento.
 *
 * @param {number|string} id - ID del medicamento.
 * @param {Object} data - Datos a actualizar.
 * @returns {Promise<Object>} Resultado de la actualizaci?n.
 */
export const updateDrug = async (id, data) => {
    return farmavel.put(`v1/drugs/${id}`, data)
}

/**
 * Crea un nuevo medicamento.
 *
 * @param {Object} data - Datos del medicamento.
 * @returns {Promise<Object>} Medicamento creado.
 */
export const createDrug = async (data) => {
    return farmavel.post('v1/drugs', data)
}

/**
 * Elimina un medicamento.
 *
 * @param {number|string} id - ID del medicamento.
 * @returns {Promise<Object>} Resultado de la eliminaci?n.
 */
export const deleteDrug = async (id) => {
    return farmavel.delete(`v1/drugs/${id}`)
}

export const getDrugDosingRules = async (drugId) => {
    return farmavel.get(`v1/drugs/${drugId}/dosing-rules`)
}

export const createDrugDosingRule = async (drugId, data) => {
    return farmavel.post(`v1/drugs/${drugId}/dosing-rules`, data)
}

export const updateDrugDosingRule = async (drugId, dosingRuleId, data) => {
    return farmavel.put(`v1/drugs/${drugId}/dosing-rules/${dosingRuleId}`, data)
}

export const deleteDrugDosingRule = async (drugId, dosingRuleId) => {
    return farmavel.delete(`v1/drugs/${drugId}/dosing-rules/${dosingRuleId}`)
}

/**
 * Solicita un token temporal para la exportación de medicamentos.
 *
 * @returns {Promise<Object>} Objeto con el token de acceso.
 */
export const getExportTokenForDrugs = async () => {
    return farmavel.post('v1/security/export-token', { type: 'drugs' })
}

/**
 * Genera token temporal para descargar la matriz de probióticos.
 * @returns {Promise<Object>} Objeto con el token de acceso.
 */
export const getExportTokenForProbiotics = async () => {
    return farmavel.post('v1/security/export-token', { type: 'probiotics' })
}

/**
 * Importa la matriz de probióticos desde un archivo Excel.
 * @param {FormData} formData - FormData con el campo 'file' conteniendo el .xlsx.
 * @returns {Promise<Object>} Resumen: { imported, updated, errors }.
 */
export const importProbiotics = async (formData) => {
    return farmavel.postForm('v1/admin/probiotics/import', formData)
}

/**
 * Obtiene la lista de citas programadas (agenda).
 *
 * @param {Object} params - Par?metros de filtrado (pharm_id, date_from, date_to).
 * @returns {Promise<Object>} Lista de citas.
 */
export const getDatesAdmin = async (params = {}) => {
    return farmavel.get('v1/admin/dates', params)
}

/**
 * Obtiene la lista de servicios realizados.
 *
 * @param {Object} params - Par?metros de filtrado (pharm_id, date_from, date_to, service_type).
 * @returns {Promise<Object>} Lista de servicios.
 */
export const getServices = async (params = {}) => {
    return farmavel.get('v1/admin/services', params)
}

/**
 * Solicita un token temporal para la exportaci?n de servicios.
 *
 * @param {Object} params - Par?metros de filtrado.
 * @returns {Promise<Object>} Objeto con el token de acceso.
 */
export const getExportToken = async (params = {}) => {
    return farmavel.post('v1/security/export-token', params)
}

/**
 * Exporta los servicios en formato Excel.
 *
 * @param {Object} params - Par?metros de filtrado.
 * @returns {Promise<Blob>} El archivo Excel como blob.
 */
export const exportServices = async (params = {}) => {
    return farmavel.get('v1/admin/services/export', params, {
        responseType: 'blob',
    })
}

/**
 * Agrega o actualiza servicios en una farmacia.
 *
 * @param {Object} data - Datos de los servicios a agregar/actualizar.
 * @returns {Promise<Object>} Resultado de la operaci?n.
 */
export const updatePharmServices = async (data) => {
    return farmavel.post('v1/admin/pharms/services', data)
}

/**
 * Elimina un servicio de una farmacia.
 *
 * @param {number|string} id - ID del servicio de la farmacia (ps_id).
 * @returns {Promise<Object>} Resultado de la operaci?n.
 */
export const removePharmService = async (id) => {
    return farmavel.delete(`v1/admin/pharms/services/${id}`)
}

/**
 * Agrega o actualiza un miembro del personal de una farmacia.
 *
 * @param {Object} data - Datos del personal (pharm_id, row_id y campos staff_*).
 * @returns {Promise<Object>} Resultado de la operación con la lista actualizada.
 */
export const updatePharmStaff = async (data) => {
    return farmavel.post('v1/admin/pharms/staff', data)
}

/**
 * Elimina un miembro del personal de una farmacia.
 *
 * @param {number|string} id - ID del miembro del personal (staff_id).
 * @param {number|string} pharmId - ID de la farmacia a la que pertenece.
 * @returns {Promise<Object>} Resultado de la operación con la lista actualizada.
 */
export const removePharmStaff = async (id, pharmId) => {
    return farmavel.delete(`v1/admin/pharms/staff/${id}`, {
        params: { pharm_id: pharmId },
    })
}

/**
 * Elimina un producto de una farmacia.
 *
 * @param {number|string} id - ID del producto de la farmacia (prod_id).
 * @returns {Promise<Object>} Resultado de la operaci?n.
 */
export const removeProduct = async (id) => {
    return farmavel.delete(`v1/admin/products/${id}`)
}

export const createProduct = async (data) => {
    return farmavel.post('v1/admin/products', data)
}

export const updateProduct = async (id, data) => {
    return farmavel.put(`v1/admin/products/${id}`, data)
}

// ─── MATRIZ DE PROBIÓTICOS ────────────────────────────────────────────────────

export const getProbiotics = (params = {}) =>
    farmavel.get('v1/admin/probiotics', params)

export const getProbiotic = (id) =>
    farmavel.get(`v1/admin/probiotics/${id}`)

export const getProbioticData = getProbiotic // alias de compatibilidad

export const createProbiotic = (data) =>
    farmavel.post('v1/admin/probiotics', data)

export const updateProbiotic = (id, data) =>
    farmavel.put(`v1/admin/probiotics/${id}`, data)

export const removeProbiotic = (id) =>
    farmavel.delete(`v1/admin/probiotics/${id}`)

export const deleteProbiotic = removeProbiotic // alias de compatibilidad

// Indicaciones del probiótico (pivot)
export const getProbioticIndications = (probId) =>
    farmavel.get(`v1/admin/probiotics/${probId}/indications`)

export const addProbioticIndication = (probId, data) =>
    farmavel.post(`v1/admin/probiotics/${probId}/indications`, data)

export const updateProbioticIndication = (probId, linkId, data) =>
    farmavel.put(`v1/admin/probiotics/${probId}/indications/${linkId}`, data)

export const removeProbioticIndication = (probId, linkId) =>
    farmavel.delete(`v1/admin/probiotics/${probId}/indications/${linkId}`)

// Historial de revisiones del probiótico
export const getProbioticReviews = (probId) =>
    farmavel.get(`v1/admin/probiotics/${probId}/reviews`)

export const addProbioticReview = (probId, data) =>
    farmavel.post(`v1/admin/probiotics/${probId}/reviews`, data)

// Probióticos de farmacia — Etapa 05
export const getPharmProbiotics = (pharmId) =>
    farmavel.get(`v1/admin/pharms/${pharmId}/probiotics`)

// Cola de probióticos reportados por farmacias que aún no están en la matriz
export const getReportedProbiotics = () =>
    farmavel.get('v1/admin/reported-probiotics')

export const importPharmProbiotics = (pharmId, formData) =>
    farmavel.post(`v1/admin/pharms/${pharmId}/probiotics/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })

// Confirma una equivalencia sugerida por el match difuso de nombres: vincula la
// fila pendiente de la farmacia con un probiótico de la matriz maestra.
export const linkPharmProbiotic = (pharmId, ppId, probId) =>
    farmavel.post(`v1/admin/pharms/${pharmId}/probiotics/${ppId}/link`, { prob_id: probId })

// ─── INDICACIONES MAESTRAS ───────────────────────────────────────────────────

export const getProbioticMasterIndications = (params = {}) =>
    farmavel.get('v1/admin/probiotic-master-indications', params)

export const createProbioticMasterIndication = (data) =>
    farmavel.post('v1/admin/probiotic-master-indications', data)

export const updateProbioticMasterIndication = (id, data) =>
    farmavel.put(`v1/admin/probiotic-master-indications/${id}`, data)

// ─── LABORATORIOS ────────────────────────────────────────────────────────────

export const getLaboratories = (params = {}) =>
    farmavel.get('v1/admin/laboratories', params)

export const createLaboratory = (data) =>
    farmavel.post('v1/admin/laboratories', data)

export const updateLaboratory = (id, data) =>
    farmavel.put(`v1/admin/laboratories/${id}`, data)

export const deleteLaboratory = (id) =>
    farmavel.delete(`v1/admin/laboratories/${id}`)

// ─── PRESENTACIONES DE PROBIÓTICOS ───────────────────────────────────────────

export const getProbioticPresentations = (params = {}) =>
    farmavel.get('v1/admin/probiotic-presentations', params)

export const createProbioticPresentation = (data) =>
    farmavel.post('v1/admin/probiotic-presentations', data)

export const updateProbioticPresentation = (id, data) =>
    farmavel.put(`v1/admin/probiotic-presentations/${id}`, data)

export const deleteProbioticPresentation = (id) =>
    farmavel.delete(`v1/admin/probiotic-presentations/${id}`)

// ─── DOSIS DE PROBIÓTICOS ────────────────────────────────────────────────────

export const getProbioticDoses = (params = {}) =>
    farmavel.get('v1/admin/probiotic-doses', params)

export const createProbioticDose = (data) =>
    farmavel.post('v1/admin/probiotic-doses', data)

export const updateProbioticDose = (id, data) =>
    farmavel.put(`v1/admin/probiotic-doses/${id}`, data)

export const deleteProbioticDose = (id) =>
    farmavel.delete(`v1/admin/probiotic-doses/${id}`)

// ─── CONSULTAS PROBIOMENTOR (admin) ──────────────────────────────────────────

export const getProbiomentorConsultations = (params = {}) =>
    farmavel.get('v1/admin/probiomentor/consultations', params)

/**
 * Material Divulgativo — carpetas y archivos anidados, filtrados por
 * servicio en la app de farmacia. Gestión completa solo desde el admin.
 */

/**
 * Obtiene el árbol completo de carpetas de material (todas, sin filtrar).
 *
 * @returns {Promise<Object>} Árbol anidado { id, name, slug, order, active, services, files, children }.
 */
export const getMaterialsTree = async () => {
    return farmavel.get('v1/admin/materials/tree')
}

/**
 * Crea una carpeta de material. Si `mf_parent_id` es null es una carpeta
 * raíz y puede llevar `services` (array de códigos SATI/IRETIA/CEPOC/...);
 * en subcarpetas los servicios se ignoran en el backend.
 *
 * @param {{mf_name: string, mf_parent_id?: number|null, services?: string[]}} data
 * @returns {Promise<Object>} Carpeta creada.
 */
export const createFolder = async (data) => {
    return farmavel.post('v1/admin/materials/folders', data)
}

/**
 * Actualiza nombre/orden/activo y (si es raíz) los servicios de una carpeta.
 *
 * @param {number|string} id - mf_id de la carpeta.
 * @param {Object} data
 * @returns {Promise<Object>} Carpeta actualizada.
 */
export const updateFolder = async (id, data) => {
    return farmavel.put(`v1/admin/materials/folders/${id}`, data)
}

/**
 * Elimina (soft-delete) una carpeta junto con sus subcarpetas y archivos.
 *
 * @param {number|string} id - mf_id de la carpeta.
 * @returns {Promise<Object>} Resultado de la eliminación.
 */
export const deleteFolder = async (id) => {
    return farmavel.delete(`v1/admin/materials/folders/${id}`)
}

/**
 * Sube uno o más archivos a una carpeta.
 *
 * @param {number|string} folderId - mf_id de la carpeta destino.
 * @param {File[]} files
 * @returns {Promise<Object>} Archivos creados.
 */
export const uploadMaterials = async (folderId, files) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files[]', file))

    return farmavel.postForm(`v1/admin/materials/folders/${folderId}/files`, formData)
}

/**
 * Sube/actualiza la portada (imagen) de una carpeta de material.
 *
 * @param {number|string} folderId - mf_id de la carpeta destino.
 * @param {File} cover
 * @returns {Promise<Object>} Resultado de la subida.
 */
export const uploadFolderCover = async (folderId, cover) => {
    const formData = new FormData()
    formData.append('cover', cover)

    return farmavel.postForm(
        `v1/admin/materials/folders/${folderId}/cover`,
        formData
    )
}

/**
 * Elimina (fila + archivo físico en disco) un archivo de material.
 *
 * @param {number|string} id - mfile_id del archivo.
 * @returns {Promise<Object>} Resultado de la eliminación.
 */
export const deleteMaterial = async (id) => {
    return farmavel.delete(`v1/admin/materials/files/${id}`)
}

/**
 * Sube un árbol de carpetas completo de una sola vez (sin archivos): crea o
 * reutiliza cada carpeta de forma idempotente. Pensado para cuando el
 * usuario selecciona una carpeta local con subcarpetas y quiere replicar esa
 * estructura sin crearla a mano nivel por nivel.
 *
 * @param {number|null} parentId - mf_id bajo el que se crea el árbol, o null para raíz.
 * @param {string[]} paths - rutas relativas de cada carpeta (todas, no solo hojas), p.ej. ["Cigarrillo", "Cigarrillo/Carousel-prevencion"].
 * @returns {Promise<Object>} { folderIds: { [ruta]: mf_id } }
 */
export const createFolderTree = async (parentId, paths) => {
    return farmavel.post('v1/admin/materials/tree', { parent_id: parentId, paths })
}
